import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Trash2, Plus, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface ConsultantTeacher {
  id: string;
  name: string;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
}

export function ConsultantTeachersDialog({ open, onOpenChange, onChanged }: Props) {
  const [items, setItems] = useState<ConsultantTeacher[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('consultant_teachers')
      .select('id, name, is_active')
      .order('name');
    if (error) {
      toast({ title: 'โหลดรายชื่อไม่สำเร็จ', description: error.message, variant: 'destructive' });
    } else {
      setItems((data || []) as ConsultantTeacher[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    const { error } = await (supabase as any).from('consultant_teachers').insert({ name });
    setSaving(false);
    if (error) {
      toast({ title: 'เพิ่มไม่สำเร็จ', description: error.message, variant: 'destructive' });
      return;
    }
    setNewName('');
    await load();
    onChanged?.();
  };

  const handleRename = async (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const { error } = await (supabase as any).from('consultant_teachers').update({ name: trimmed }).eq('id', id);
    if (error) toast({ title: 'แก้ไขไม่สำเร็จ', description: error.message, variant: 'destructive' });
    else onChanged?.();
  };

  const handleToggle = async (id: string, is_active: boolean) => {
    const { error } = await (supabase as any).from('consultant_teachers').update({ is_active }).eq('id', id);
    if (error) {
      toast({ title: 'อัปเดตไม่สำเร็จ', description: error.message, variant: 'destructive' });
      return;
    }
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, is_active } : it)));
    onChanged?.();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('ยืนยันการลบครูที่ปรึกษานี้?')) return;
    const { error } = await (supabase as any).from('consultant_teachers').delete().eq('id', id);
    if (error) {
      toast({ title: 'ลบไม่สำเร็จ', description: error.message, variant: 'destructive' });
      return;
    }
    setItems((prev) => prev.filter((it) => it.id !== id));
    onChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>จัดการครูที่ปรึกษา</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="ชื่อครูที่ปรึกษาใหม่"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
            <Button onClick={handleAdd} disabled={saving || !newName.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">ยังไม่มีรายชื่อ</p>
          ) : (
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="flex items-center gap-2 border rounded-md p-2">
                  <Input
                    defaultValue={it.name}
                    onBlur={(e) => {
                      if (e.target.value !== it.name) handleRename(it.id, e.target.value);
                    }}
                    className="flex-1"
                  />
                  <div className="flex items-center gap-1">
                    <Switch checked={it.is_active} onCheckedChange={(v) => handleToggle(it.id, v)} />
                    <span className="text-xs text-muted-foreground w-12">{it.is_active ? 'ใช้งาน' : 'ปิด'}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(it.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}